import React from "react"
import { Box, Text } from "@chakra-ui/react"
import {
  BadgeElement,
  BadgeElementType,
  BadgeFieldData,
  BADGE_TEXT_SOURCE_LABELS,
} from "sharedTypes/badge"
import {
  EventData,
  GuestData,
  resolveBadgeTextSource,
} from "../utils/badgeDataResolver"
import { EDITOR_RESOLUTION } from "../utils/constants"
import { BADGE_FONT_WEIGHTS } from "../utils/badgeFonts"

type Props = {
  element: BadgeElement
  scaleFactor?: number
  guestData?: GuestData
  eventData?: EventData
}

const BadgeField = ({
  element,
  scaleFactor = EDITOR_RESOLUTION,
  guestData,
  eventData,
}: Props) => {
  if (element.type !== BadgeElementType.FIELD) return null

  const data = element.data as BadgeFieldData

  // Use the resolver to get the actual display text
  const displayText = resolveBadgeTextSource(
    data.source,
    undefined, // Field elements don't have staticText
    guestData,
    eventData,
  )

  // Show placeholder only when we're in design mode without real data
  // When we have real guest/event data, show the actual value (even if empty)
  const shouldShowPlaceholder = !guestData && !eventData
  const placeholderText = shouldShowPlaceholder
    ? `[${BADGE_TEXT_SOURCE_LABELS[data.source] || "Select field"}]`
    : displayText

  const fontSizeInInches = data.fontSize * (1 / 72) // pt to inches
  const fontSizeInMm = fontSizeInInches * 25.4 // pt to mm
  const scaledFontSize = fontSizeInMm * scaleFactor // Scale like canvas

  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems={
        data.verticalAlign === "top"
          ? "flex-start"
          : data.verticalAlign === "bottom"
            ? "flex-end"
            : "center"
      }
      justifyContent={
        data.align === "center"
          ? "center"
          : data.align === "right"
            ? "flex-end"
            : data.align === "justify"
              ? "center"
              : "flex-start"
      }
      overflow="visible"
      p={0}
      m={0}
    >
      <Text
        fontSize={`${scaledFontSize}px`}
        fontFamily={data.font}
        color={shouldShowPlaceholder ? "#999" : data.color}
        fontWeight={data.weight || BADGE_FONT_WEIGHTS.Regular}
        fontStyle={shouldShowPlaceholder ? "italic" : (data.italic ? "italic" : "normal")}
        textDecoration={
          shouldShowPlaceholder ? "none" : (
            data.textDecoration === "underline"
              ? "underline"
              : data.textDecoration === "strikethrough"
                ? "line-through"
                : "none"
          )
        }
        textTransform={
          shouldShowPlaceholder ? "none" : (
            data.textTransform === "capitalize"
              ? "capitalize"
              : data.textTransform === "uppercase"
                ? "uppercase"
                : data.textTransform === "lowercase"
                  ? "lowercase"
                  : "none"
          )
        }
        lineHeight="1"
        textAlign={data.align === "justify" ? "justify" : data.align}
        w={data.align === "justify" ? "full" : "auto"}
        whiteSpace="pre-wrap"
        p={0}
        m={0}
      >
        {placeholderText}
      </Text>
    </Box>
  )
}

export default BadgeField
